import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Globe2, Radio, TrendingUp, MapPin, Info } from 'lucide-react';

/**
 * "Where customers come from" — acquisition channel, geography, and the signup
 * waves that stand in for per-creator attribution until we have real per-link
 * tracking.
 *
 * Color, per the data-viz method:
 *   - The channel and geography panels rank magnitudes, so they use ONE hue at
 *     varying opacity (sequential) and let the row label carry identity. No
 *     categorical palette is needed, so there is no colorblind risk to manage.
 *   - The wave chart is the emphasis form: the selected channel takes the accent
 *     hue, every other signup is de-emphasized gray. That keeps "is this week
 *     unusual for TikTok" readable at a glance instead of asking the eye to
 *     separate seven stacked hues.
 *   - `--acq-accent` (#3987e5) was validated against this page's near-black
 *     surface (dark mode, contrast ≥ 3:1) rather than picked by eye.
 *
 * Mobile: every panel is a single column of full-width rows with horizontal
 * bars — long metro names ("Dallas–Fort Worth") stay readable on a 375px screen,
 * where vertical bars would force either rotated labels or a sideways scroll.
 * Nothing here may introduce page-level horizontal scroll.
 */

const ACCENT = '#3987e5';

/** Weeks of history the wave chart shows on a phone. A year of columns inside a
 *  343px card is 3.6px each — too thin to read and far too thin to tap. Half a
 *  year gives ~12px columns, which is both. Detection still runs over the FULL
 *  history; only the drawn window narrows, and the axis labels say so. */
const NARROW_WEEK_WINDOW = 26;

function useIsNarrow() {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(max-width: 639px)');
    const onChange = (e) => setNarrow(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return narrow;
}

const fmtPct = (n) => `${Math.round((n || 0) * 100)}%`;
const fmtWeek = (iso) => {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

/** Ranked horizontal bars. Sequential opacity by magnitude; label carries identity. */
function RankedBars({ rows, total, emptyLabel, suffix }) {
  if (!rows?.length) {
    return <p className="text-white/40 text-sm font-normal py-6 text-center">{emptyLabel}</p>;
  }
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <div className="space-y-2.5">
      {rows.map((r, i) => {
        const pctOfMax = (r.count / max) * 100;
        // Sequential: the leader is fully saturated, the tail recedes — but
        // never below 0.35 or the smallest rows stop reading as bars at all.
        const opacity = Math.max(0.35, 1 - i * 0.075);
        return (
          <div key={r.key} className="group">
            <div className="flex items-baseline justify-between gap-3 mb-1">
              <span className="text-white/85 text-[13px] font-normal truncate min-w-0">{r.key}</span>
              <span className="text-white/55 text-[12px] font-normal tabular-nums whitespace-nowrap flex-shrink-0">
                {r.count}
                {total ? <span className="text-white/30"> · {fmtPct(r.count / total)}</span> : null}
                {suffix ? <span className="text-white/30"> {suffix}</span> : null}
              </span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-white/[0.06] overflow-hidden">
              <div
                className="h-full rounded-full transition-[width] duration-700 ease-out"
                style={{ width: `${pctOfMax}%`, backgroundColor: ACCENT, opacity }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Weekly signup columns in the emphasis form: selected channel vs everything else. */
function WaveChart({ weeks, channel, channelLabel, waveWeeks }) {
  const [hover, setHover] = useState(null);
  const isNarrow = useIsNarrow();
  const data = useMemo(() => {
    const all = (weeks || []).map((w) => ({
      week: w.week,
      focus: w.byChannel?.[channel] || 0,
      total: w.total || 0,
    }));
    return isNarrow ? all.slice(-NARROW_WEEK_WINDOW) : all;
  }, [weeks, channel, isNarrow]);

  if (!data.length) return null;
  const max = Math.max(...data.map((d) => d.total), 1);
  const waveSet = new Set(waveWeeks || []);
  const active = hover != null ? data[hover] : null;

  return (
    <div>
      {/* Fixed height so the row of columns never reflows as the channel changes. */}
      <div className="relative h-[132px] flex items-end gap-[3px] sm:gap-1">
        {data.map((d, i) => {
          const isWave = waveSet.has(d.week);
          const totalH = Math.max(2, (d.total / max) * 100);
          const focusH = Math.max(d.focus > 0 ? 2 : 0, (d.focus / max) * 100);
          return (
            <button
              key={d.week}
              type="button"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(i)}
              onBlur={() => setHover(null)}
              /* The column is thin, so the hit target is the full-height wrapper
                 rather than the bar — comfortably tappable on a phone. */
              className="relative flex-1 h-full flex flex-col justify-end min-w-0 group"
              aria-label={`Week of ${fmtWeek(d.week)}: ${d.focus} ${channelLabel}, ${d.total} total`}
            >
              <div className="relative w-full" style={{ height: `${totalH}%` }}>
                {/* Context: everything that is not the selected channel. */}
                <div className="absolute inset-0 rounded-t-[3px] bg-white/[0.13] group-hover:bg-white/20 transition-colors" />
                {/* Emphasis: the selected channel, anchored to the baseline. */}
                <div
                  className="absolute bottom-0 left-0 right-0 rounded-t-[3px] transition-[height] duration-500"
                  style={{
                    height: `${d.total ? (focusH / totalH) * 100 : 0}%`,
                    backgroundColor: ACCENT,
                    opacity: hover == null || hover === i ? 1 : 0.55,
                  }}
                />
              </div>
              {isWave && (
                <span
                  className="absolute -top-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: ACCENT, boxShadow: `0 0 8px ${ACCENT}` }}
                  aria-hidden="true"
                />
              )}
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between mt-2 text-[11px] text-white/35 font-normal tabular-nums">
        <span>{fmtWeek(data[0].week)}</span>
        {active ? (
          <span className="text-white/70">
            {fmtWeek(active.week)} · <span style={{ color: ACCENT }}>{active.focus}</span> of {active.total}
          </span>
        ) : (
          <span className="text-white/30">
            {isNarrow ? `last ${data.length} weeks` : 'weekly signups'}
          </span>
        )}
        <span>{fmtWeek(data[data.length - 1].week)}</span>
      </div>

      <div className="flex items-center gap-4 mt-3">
        <span className="inline-flex items-center gap-1.5 text-[11px] text-white/55 font-normal">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: ACCENT }} />
          {channelLabel}
        </span>
        <span className="inline-flex items-center gap-1.5 text-[11px] text-white/40 font-normal">
          <span className="w-2.5 h-2.5 rounded-sm bg-white/[0.13]" />
          all other signups
        </span>
        <span className="inline-flex items-center gap-1.5 text-[11px] text-white/40 font-normal ml-auto">
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: ACCENT }} />
          wave
        </span>
      </div>
    </div>
  );
}

export default function AcquisitionGeography({ data, loading }) {
  const [geoView, setGeoView] = useState('booked');
  const [channel, setChannel] = useState('tiktok');

  const channels = data?.channels || [];
  const summary = data?.summary || {};
  const waves = data?.waves?.[channel] || [];
  const waveWeeks = waves.map((w) => w.week);

  const geo = data?.geography || {};
  const geoRows = geoView === 'booked' ? geo.booked_metros
    : geoView === 'searched' ? geo.searched_metros
      : geo.billing_states;
  const geoTotal = (geoRows || []).reduce((s, r) => s + r.count, 0);

  if (loading) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
        <div className="h-5 w-48 bg-white/10 rounded animate-pulse mb-6" />
        <div className="space-y-3">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-3 bg-white/5 rounded animate-pulse" />)}
        </div>
      </div>
    );
  }
  if (!data) return null;

  const tiles = [
    { label: 'Signups', value: summary.total_users?.toLocaleString() ?? '—', sub: 'all time' },
    { label: 'Told us how', value: fmtPct(summary.attribution_rate), sub: `${(summary.attributed_users || 0).toLocaleString()} people` },
    { label: 'TikTok share', value: fmtPct(summary.tiktok_share_of_attributed), sub: `${summary.tiktok_count || 0} of those who answered` },
    { label: 'Waves found', value: String((data.waves?.tiktok || []).length), sub: 'TikTok bursts' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05 }}
      className="mb-12"
    >
      <div className="flex items-center gap-2 mb-1">
        <Globe2 className="w-4 h-4 text-white/40" />
        <h2 className="text-white font-light text-xl tracking-tight">Where customers come from</h2>
      </div>
      <p className="text-white/45 text-[13px] font-normal mb-5 max-w-2xl leading-snug">
        Self-reported channel, the cities customers fly out of, and the billing states behind
        their cards — lined up against signup timing to spot creator-driven bursts.
      </p>

      {/* KPI row — 2-up on phones, 4-up from sm. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {tiles.map((t) => (
          <div key={t.label} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
            <p className="text-[10px] uppercase tracking-widest text-white/40 font-normal mb-1.5">{t.label}</p>
            <p className="text-white text-2xl font-light tracking-tight leading-none mb-1.5">{t.value}</p>
            <p className="text-white/35 text-[11px] font-normal leading-tight">{t.sub}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* ── Channel mix ─────────────────────────────────────────────── */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
          <div className="flex items-center gap-2 mb-1">
            <Radio className="w-3.5 h-3.5 text-white/40" />
            <h3 className="text-white text-sm font-medium">How they found us</h3>
          </div>
          <p className="text-white/35 text-[11px] font-normal mb-4">
            Share of the {(summary.attributed_users || 0).toLocaleString()} who answered at signup —
            not of all {(summary.total_users || 0).toLocaleString()} accounts.
          </p>
          <RankedBars rows={channels.map((c) => ({ key: c.label, count: c.count }))} total={summary.attributed_users} emptyLabel="No channel data yet" />
          {summary.attribution_rate < 0.9 && (
            <p className="mt-4 pt-3 border-t border-white/[0.06] text-white/40 text-[11px] font-normal leading-snug flex gap-2">
              <Info className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
              <span>
                {fmtPct(1 - (summary.attribution_rate || 0))} of accounts never answered, so treat
                these as the shape of the answered half rather than the whole business.
              </span>
            </p>
          )}
        </div>

        {/* ── Geography ───────────────────────────────────────────────── */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-3.5 h-3.5 text-white/40" />
            <h3 className="text-white text-sm font-medium">Where they are</h3>
          </div>
          {/* Segmented control — 44px tall for thumbs. */}
          <div className="flex gap-1.5 mb-4 p-1 rounded-full bg-white/[0.04]">
            {[
              { id: 'booked', label: 'Booked' },
              { id: 'searched', label: 'Searched' },
              { id: 'billing', label: 'Billing' },
            ].map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => setGeoView(v.id)}
                className={`flex-1 min-h-[36px] px-3 rounded-full text-[12px] font-medium transition-all ${
                  geoView === v.id ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white/75'
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>
          <RankedBars
            rows={geoRows || []}
            total={geoTotal}
            emptyLabel={geoView === 'billing'
              ? 'No billing geography cached yet — run scripts/backfill-customer-geo.mjs'
              : 'No geography data yet'}
          />
          <p className="mt-4 pt-3 border-t border-white/[0.06] text-white/35 text-[11px] font-normal leading-snug">
            {geoView === 'booked' && 'One count per customer — their most frequent departure metro across booked trips, so a frequent flyer can’t move a metro’s rank alone. Multi-airport metros are merged — JFK, LGA and EWR all read New York.'}
            {geoView === 'searched' && 'One count per customer — their most frequent search-departure city among searches that never became a booking. Repeat searches by the same person count once.'}
            {geoView === 'billing' && (
              <>
                Billing state on the card, projected from NMI vaults and Stripe charges for{' '}
                {summary.billing_geo_users || 0} customers.
                {summary.billing_geo_derived > 0 && ` ${summary.billing_geo_derived} inferred from ZIP.`}
              </>
            )}
          </p>
        </div>
      </div>

      {/* ── Waves ───────────────────────────────────────────────────────── */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-3 mb-1">
          <TrendingUp className="w-3.5 h-3.5 text-white/40" />
          <h3 className="text-white text-sm font-medium">Signup waves</h3>
          <div className="flex flex-wrap gap-1.5 w-full sm:w-auto sm:ml-auto">
            {channels.slice(0, 4).map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => setChannel(c.key)}
                className={`min-h-[32px] px-3 rounded-full text-[11px] font-medium transition-all ${
                  channel === c.key
                    ? 'bg-white/10 text-white border border-white/20'
                    : 'text-white/45 hover:text-white/70 border border-transparent'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
        <p className="text-white/35 text-[11px] font-normal mb-4 max-w-2xl leading-snug">
          A creator post shows up here as a burst — a week well above the trailing six-week median,
          usually concentrated in the metros where that creator&rsquo;s audience lives.
        </p>

        <WaveChart
          weeks={data.weeks}
          channel={channel}
          channelLabel={channels.find((c) => c.key === channel)?.label || channel}
          waveWeeks={waveWeeks}
        />

        {waves.length > 0 ? (
          <div className="mt-5 pt-4 border-t border-white/[0.06] space-y-3">
            <p className="text-[10px] uppercase tracking-widest text-white/40 font-normal">
              Detected bursts · likely creator posts
            </p>
            {waves.slice(0, 6).map((w) => (
              <div key={w.week} className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-3.5">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 mb-2.5">
                  <span className="text-white text-[13px] font-medium tabular-nums">
                    {fmtWeek(w.week)} – {fmtWeek(w.weekEnd)}
                  </span>
                  {/* A multiple computed against a baseline of zero is not a
                      multiple — those are the channel's first signups, and
                      saying "8× normal · usual week 0" reads as broken math. */}
                  <span className="text-[11px] font-medium tabular-nums px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: `${ACCENT}22`, color: ACCENT }}>
                    {w.baseline >= 1 ? `${w.multiple.toFixed(1)}× normal` : 'first activity'}
                  </span>
                  <span className="text-white/45 text-[11px] font-normal tabular-nums ml-auto">
                    {w.signupCount} signups
                    {w.baseline >= 1 ? ` · usual week ${w.baseline}` : ' · nothing the weeks before'}
                  </span>
                </div>
                {w.topMetros?.length ? (
                  <div className="flex flex-wrap gap-1.5">
                    {w.topMetros.map((m) => (
                      <span
                        key={m.key}
                        className="inline-flex items-baseline gap-1.5 text-[11px] font-normal px-2 py-1 rounded-lg bg-white/[0.04] text-white/70"
                      >
                        {m.key}
                        <span className="text-white/40 tabular-nums">{fmtPct(m.share)}</span>
                        {m.lift && m.lift >= 1.5 && (
                          /* Lift is the whole point: 12x means this burst was
                             far more Boston than the business normally is. */
                          <span className="tabular-nums font-medium" style={{ color: ACCENT }}>
                            {m.lift.toFixed(1)}×
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-white/30 text-[11px] font-normal">No location known for these signups yet.</p>
                )}
              </div>
            ))}
            <p className="text-white/35 text-[11px] font-normal leading-snug pt-1">
              The × next to a metro is its lift — how much more concentrated it is in this burst than
              across all signups. Two bursts with different metro mixes are probably different creators;
              a repeat of the same mix is probably the same one posting again. This is inference, not
              attribution — a per-creator link or promo code would make it exact.
            </p>
          </div>
        ) : (
          <p className="mt-5 pt-4 border-t border-white/[0.06] text-white/40 text-[12px] font-normal">
            No bursts detected for this channel — signups have tracked close to their usual weekly pace.
          </p>
        )}
      </div>
    </motion.div>
  );
}
